<script setup>
import { Link, useForm } from "@inertiajs/vue3"
import AppLayout from "@/Layouts/AppLayout.vue"
import PageShell from "@/Components/UI/PageShell.vue"
import CustomerChannels from "@/Components/CustomerChannels.vue"
import PhoneInput from "@/Components/UI/PhoneInput.vue"
import UiButton from "@/Components/UI/UiButton.vue"
import StickyActionBar from "@/Components/UI/StickyActionBar.vue"
import Icon from "@/Components/UI/Icon.vue"
import { DEFAULT_PLATFORMS } from "@/composables/useChannelConfig.js"
import { NATIONALITIES } from "@/composables/useNationalities.js"

defineOptions({ layout: AppLayout })

const props = defineProps({
  customer: { type: Object, required: true },
  channels: { type: Array, default: () => [] },
  errors:   { type: Object, default: () => ({}) },
  defaults: { type: Object, default: () => ({}) },
})

const isEdit = !!props.customer.id

function seedChannels(channels) {
  const result = [...channels]
  for (const platform of DEFAULT_PLATFORMS) {
    if (!result.find(c => c.platform === platform)) {
      result.push({ platform, handle: "", custom_label: "", position: DEFAULT_PLATFORMS.indexOf(platform) })
    }
  }
  return result
}

const form = useForm({
  first_name:                   props.customer.first_name ?? "",
  last_name:                    props.customer.last_name ?? "",
  email:                        props.customer.email ?? "",
  phone:                        props.customer.phone ?? "",
  date_of_birth:                props.customer.date_of_birth ?? "",
  nationality:                  props.customer.nationality ?? "",
  passport_no:                  props.customer.passport_no ?? "",
  preferred_locale:             props.customer.preferred_locale ?? "",
  licence_number:               props.customer.licence_number ?? "",
  licence_expiry:               props.customer.licence_expiry ?? "",
  address:                      props.customer.address ?? "",
  notes:                        props.customer.notes ?? "",
  licence_scan:                 null,
  passport_photo:               null,
  contact_channels_attributes: seedChannels(props.channels),
  return_to:                    props.defaults?.return_to ?? "",
  link_conversation:            props.defaults?.link_conversation ?? "",
})

function submit() {
  form.transform((data) => {
    const { return_to, link_conversation, ...customerData } = data
    return {
      customer: {
        ...customerData,
        contact_channels_attributes: customerData.contact_channels_attributes
          .filter(c => c.handle !== "" || c._destroy)
          .map((c, i) => ({ ...c, position: i })),
      },
      return_to,
      link_conversation,
    }
  })
  if (isEdit) {
    form.patch(`/customers/${props.customer.id}`, { forceFormData: true, replace: true })
  } else {
    form.post("/customers", { forceFormData: true })
  }
}

function err(field) {
  return form.errors[field] || props.errors[field]?.[0]
}
</script>

<template>
  <PageShell :title="isEdit ? 'Edit Customer' : 'New Customer'">
    <div class="max-w-2xl mx-auto w-full">
      <Link
        href="/customers"
        class="inline-flex items-center text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors mb-4"
        aria-label="Back"
      >
        <Icon name="chev-l" :size="18" />
      </Link>
      <form
        id="customer-form"
        @submit.prevent="submit"
        class="rounded-[var(--radius-lg)] border border-[color:var(--line)] bg-[color:var(--surface)] p-6 space-y-5"
      >
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">First Name *</label>
            <input
              v-model="form.first_name"
              type="text"
              required
              class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              :class="err('first_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
            />
            <p v-if="err('first_name')" class="mt-1 text-xs text-[color:var(--danger)]">{{ err("first_name") }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Last Name *</label>
            <input
              v-model="form.last_name"
              type="text"
              required
              class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
              :class="err('last_name') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
            />
            <p v-if="err('last_name')" class="mt-1 text-xs text-[color:var(--danger)]">{{ err("last_name") }}</p>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Email *</label>
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full rounded-[var(--radius-md)] border bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            :class="err('email') ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
          />
          <p v-if="err('email')" class="mt-1 text-xs text-[color:var(--danger)]">{{ err("email") }}</p>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Phone</label>
            <PhoneInput v-model="form.phone" />
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Date of Birth</label>
            <input
              v-model="form.date_of_birth"
              type="date"
              class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            />
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Nationality</label>
            <select
              v-model="form.nationality"
              class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            >
              <option value="">—</option>
              <option v-for="n in NATIONALITIES" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Passport Number</label>
            <input
              v-model="form.passport_no"
              type="text"
              class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">
            Communication language
            <span class="text-[color:var(--muted)] font-normal">— language we'll use when messaging this customer (driver-on-the-way, etc.)</span>
          </label>
          <select
            v-model="form.preferred_locale"
            class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
          >
            <option value="">Auto (brand default)</option>
            <option value="en">English</option>
            <option value="ru">Русский</option>
            <option value="th">ไทย</option>
            <option value="zh">中文</option>
          </select>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Licence Number</label>
            <input
              v-model="form.licence_number"
              type="text"
              class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Licence Expiry</label>
            <input
              v-model="form.licence_expiry"
              type="date"
              class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Licence Scan</label>
            <input
              type="file"
              accept="image/*,.pdf"
              class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
              @change="form.licence_scan = $event.target.files[0]"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Passport Photo</label>
            <input
              type="file"
              accept="image/*,.pdf"
              class="w-full text-sm text-[color:var(--inkSoft)] file:mr-3 file:py-1.5 file:px-3 file:rounded-[var(--radius-md)] file:border-0 file:text-sm file:font-medium file:bg-[color:var(--accentSoft)] file:text-[color:var(--accent)] hover:file:brightness-95 file:cursor-pointer"
              @change="form.passport_photo = $event.target.files[0]"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Address</label>
          <textarea
            v-model="form.address"
            rows="2"
            class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
          ></textarea>
        </div>

        <div>
          <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Notes</label>
          <textarea
            v-model="form.notes"
            rows="3"
            class="w-full rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
          ></textarea>
        </div>

        <div class="border-t border-[color:var(--line)] pt-4">
          <CustomerChannels v-model="form.contact_channels_attributes" />
        </div>
      </form>

      <StickyActionBar>
        <UiButton
          type="submit"
          form="customer-form"
          variant="primary"
          :loading="form.processing"
        >
          {{ form.processing ? "Saving…" : (isEdit ? "Update Customer" : "Create Customer") }}
        </UiButton>
      </StickyActionBar>
    </div>
  </PageShell>
</template>
